import { useReducer, useEffect, useRef } from "react";

/**
 * A reusable hook for managing state and methods available via a context provider
 *
 * @param initialState
 * @param reducerMap
 * @param methodHooks
 * @returns
 */
export default function useContextController<State, ContextValue>(
  initialState: State,
  reducerMap: {
    [key: string | number]: (state: State, payload: any) => State;
  },
  methodHooks: { (context: ContextValue): void }[],
  passedValues: { [key: string]: any } = {}
): ContextValue {
  /**
   * A simple reducer function that maps the state and payload a reducer function
   * defines in reducerMap
   */
  function reducer(state: State, action: any): State {
    const { type, payload } = action;

    if (type === "updateState")
      return {
        ...state,
        ...payload,
      };

    const fn = reducerMap[type];

    if (!(fn instanceof Function)) {
      console.error("invalid reducer function:", type);
      return state;
    }
    return fn(state, payload);
  }

  const [state, dispatch] = useReducer(reducer, initialState);

  // adds the current state to ref.current any time state changes
  // so functions can easily access values of the current state
  const stateRef = useRef<State>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const contextValue = {
    ...passedValues,
    ...state,
    dispatch,
    state,
    stateRef,
  } as unknown as ContextValue;

  // Here we're looping over our method hooks so
  // so each methods hook can utilize the methods defined before it
  methodHooks.forEach((hook) => {
    hook(contextValue);
  });

  // state and methods are made available to UI components
  return contextValue;
}
